import ProductVisual from "@/components/ProductVisual";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, RotateCcw, Check } from "lucide-react";
import { toast } from "sonner";
import { PREVIEW_PRODUCTS } from "@/lib/catalog";

const QUESTIONS = [
  { key: "diet", q: "How do you eat?", options: [{ v: "vegan", l: "Fully plant-based / vegan" }, { v: "flexi", l: "Flexitarian or omnivore" }] },
  { key: "avoid_egg", q: "Do you avoid eggs?", options: [{ v: "yes", l: "Yes, no eggs for me" }, { v: "no", l: "No, eggs are fine" }] },
  { key: "goal", q: "What matters most?", options: [{ v: "max-protein", l: "The highest quality protein" }, { v: "everyday", l: "Balanced everyday nutrition" }] },
  { key: "texture", q: "Preferred shake feel?", options: [{ v: "creamy", l: "Fuller & creamier" }, { v: "lightest", l: "Light & smooth" }] },
];

export default function Quiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const choose = async (key, v) => {
    const next = { ...answers, [key]: v };
    setAnswers(next);
    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      setLoading(true);
      try {
        const plant = next.diet === "vegan" || next.avoid_egg === "yes";
        setResult(PREVIEW_PRODUCTS[plant || next.goal === "everyday" ? 0 : 1]);
      } catch (e) {
        toast.error("Something went wrong. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  const restart = () => { setStep(0); setAnswers({}); setResult(null); };

  const current = QUESTIONS[step];
  const progress = result ? 100 : (step / QUESTIONS.length) * 100;

  return (
    <div className="min-h-screen bg-[#080806] pt-[70px] grain">
      <div className="mx-auto max-w-xl px-5 py-16">
        <div className="text-center">
          <p className="font-mono-clinical text-xs uppercase tracking-[0.25em] text-[#D6A921]">Find your blend</p>
          <h1 className="mt-3 font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">The 30-second match</h1>
        </div>

        <div className="mt-8 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
          <div className="h-full rounded-full bg-[#D6A921] transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>

        <div className="mt-10">
          <AnimatePresence mode="wait">
            {!result ? (
              <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }}>
                <p className="font-mono-clinical text-xs text-[#A1A1A6]">Question {step + 1} of {QUESTIONS.length}</p>
                <h2 className="mt-2 font-display text-2xl font-bold text-white">{current.q}</h2>
                <div className="mt-6 space-y-3">
                  {current.options.map((o) => (
                    <button key={o.v} onClick={() => choose(current.key, o.v)} disabled={loading} data-testid={`quiz-${current.key}-${o.v}`}
                      className="group flex w-full items-center justify-between rounded-2xl border border-white/10 bg-[#161616] p-5 text-left transition-colors hover:border-[#D6A921]">
                      <span className="font-semibold text-white">{o.l}</span>
                      <ArrowRight size={18} className="text-[#A1A1A6] transition-all group-hover:translate-x-1 group-hover:text-[#D6A921]" />
                    </button>
                  ))}
                </div>
                {step > 0 && (
                  <button onClick={() => setStep(step - 1)} className="mt-6 inline-flex items-center gap-2 text-sm text-[#A1A1A6] hover:text-white">
                    <ArrowLeft size={15} /> Back
                  </button>
                )}
              </motion.div>
            ) : (
              <motion.div key="result" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} data-testid="quiz-result">
                <p className="text-center font-mono-clinical text-xs uppercase tracking-widest text-[#D6A921]">Your match</p>
                <div className="mt-4 overflow-hidden rounded-3xl border border-white/10 bg-[#161616]">
                  <ProductVisual product={result} />
                  <div className="p-6">
                    <h3 className="font-display text-2xl font-extrabold text-white">{result.name}</h3>
                    <p className="mt-1 text-sm text-[#A1A1A6]">{result.subtitle}</p>
                    <ul className="mt-4 space-y-2">
                      {result.benefits.slice(0, 3).map((b) => (
                        <li key={b} className="flex items-start gap-2 text-sm text-[#F7F6F0]">
                          <Check size={15} className="mt-0.5 shrink-0 text-[#D6A921]" />{b}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-6 flex items-center gap-3">
                      <Link to={`/product/${result.id}`} className="flex-1 rounded-full bg-[#D6A921] py-3.5 text-center text-sm font-bold uppercase text-[#080806]">Explore your blend</Link>
                      <button onClick={() => navigate(`/product/${result.id}`)} className="rounded-full border border-white/15 px-5 py-3.5 text-sm font-bold text-white hover:border-[#D6A921]">Details</button>
                    </div>
                  </div>
                </div>
                <button onClick={restart} className="mt-6 mx-auto flex items-center gap-2 text-sm text-[#A1A1A6] hover:text-white">
                  <RotateCcw size={15} /> Retake quiz
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

